import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { OptionType } from '@europrocurement/flexy-components';
import { DossierOpenSearch, useOffreService } from '@europrocurement/l2d-domain';
import { CATEGORY_BODACC } from '@europrocurement/l2d-modules';
import { ArticlesJsonldArticleRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { enqueueSnackbar } from 'notistack';
import { DossierArticle } from '../../hooks';

export type EditBodaccFormProps = {
    onSubmit: (data: EditBodaccFormFormData) => void;
    onCancel: () => void;
    dossier: DossierOpenSearch;
    prestaArticle: DossierArticle;
};

export type EditBodaccFormFormData = {
    title: string;
    prestationId: number;
    articleId: number;
    prestaArticleId: number;
};

export type SubmittedEditBodaccFormData = {
    title: string;
    prestationId: Array<number>;
    articleId: OptionType;
    prestaArticleId: number;
};

const EditBodaccForm: React.FunctionComponent<EditBodaccFormProps> = function (props) {
    const { onSubmit, onCancel, dossier, prestaArticle } = props;
    const { articlesApi } = useOffreService();
    const [articles, setArticles] = useState<ArticlesJsonldArticleRead[]>([]);
    const [processing, setProcessing] = useState<boolean>(false);

    useEffect(() => {
        articlesApi
            .apiArticlesGetCollection({
                xIdSociete: 12,
                catArticleId: CATEGORY_BODACC,
            })
            .then((res) => {
                setArticles(res.data['hydra:member'].filter((art) => art.rubArticle === 'FRN'));
            })
            .catch((err) => console.log(err));
    }, [articlesApi]);

    const handleSubmit: SubmitHandler<SubmittedEditBodaccFormData> = (
        formData: SubmittedEditBodaccFormData,
    ) => {
        let prestationId;

        if (formData?.prestationId) {
            if (Array.isArray(formData.prestationId)) {
                [prestationId] = formData.prestationId;
            } else {
                prestationId = formData.prestationId;
            }
        }

        if (prestationId) {
            setProcessing(true);
            onSubmit({
                prestaArticleId: prestaArticle.id,
                title: formData.title,
                prestationId,
                articleId: formData?.articleId?.value as number,
            });
        } else {
            enqueueSnackbar('Formalité associée non trouvée', {
                variant: 'error',
            });
        }
    };

    const prestationChoices = dossier.formalites
        .filter((formalite) => formalite.prestationId.length > 0)
        .map((formalite) => ({
            value: formalite.prestationId[0],
            label: formalite.libelle,
        }));

    const formStructure: FormStructure[] = [
        {
            type: 'text',
            name: 'title',
            inputlabel: 'Nom de la démarche',
            rules: {
                required: 'Ce champ est requis',
            },
        },
        {
            type: 'selectItems',
            name: 'prestationId',
            mode: 'inline',
            inputlabel: 'Formalité associée',
            colInline: 6,
            items: prestationChoices,
            multiple: false,
            rules: {
                required: 'Ce champ est requis',
            },
        },
        {
            type: 'select',
            name: 'articleId',
            inputlabel: 'Article',
            options: articles.map((article) => ({
                value: article.id,
                label: article.libelle || 'article sans libelle',
            })),
            rules: {
                required: 'Ce champ est requis',
            },
        },
    ];

    const formContext = useForm<FieldValues>({
        mode: 'onChange',
        defaultValues: {
            title: prestaArticle.label,
            prestationId: prestaArticle.prestationId
                ? prestaArticle.prestationId.replace(/^\D+/g, '')
                : undefined,
            articleId: { value: prestaArticle.article.id, label: prestaArticle.article.libelle },
        },
    });

    const formProps = {
        formContext,
        onSubmit: handleSubmit,
        submitButton: {
            displayed: false,
        },
    };

    const submitBtnSx = {
        backgroundColor: 'primary.dark',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    return (
        <Box>
            <Typography variant="l2dh6">Modifier un bodacc</Typography>
            <Box
                sx={{
                    padding: '26px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '10px',
                }}
            >
                <FlexyForm
                    formStructure={formStructure}
                    {...formProps}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: '16px',
                }}
            >
                <Button
                    variant="text"
                    sx={{
                        padding: '16px 24px',
                        alignItems: 'center',
                        color: 'primary.dark',
                    }}
                    onClick={onCancel}
                    disabled={processing}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    sx={submitBtnSx}
                    onClick={formContext.handleSubmit(handleSubmit)}
                    disabled={processing}
                >
                    {processing && (
                        <CircularProgress
                            color="inherit"
                            size="16px"
                        />
                    )}
                    {!processing && (
                        <FaOptionIcon
                            icon={['fas', 'plus']}
                            sx={{ lineHeight: 0 }}
                        />
                    )}
                    Modifier le bodacc
                </Button>
            </Box>
        </Box>
    );
};

export default EditBodaccForm;
